import { useState } from 'react';

// ─── Types ───────────────────────────────────────────────────────────────────

type MenuItem = {
  name: string;
  note?: string;
  price?: string;
  variants?: string[];
};

type MenuSection = {
  id: string;
  heading: string;
  subheading?: string;
  items: MenuItem[];
  optionNote?: string;
};

type Tab = {
  id: string;
  label: string;
  images: { src: string; alt: string }[];
  sections: MenuSection[];
};

// ─── Data ────────────────────────────────────────────────────────────────────

const TABS: Tab[] = [
  {
    id: 'morning',
    label: 'Morning',
    images: [
      {
        src: 'https://storage.readdy-site.link/project_files/8e67da46-5e1d-4785-abe7-63d36243e4bc/4e6ae6b3-ffd2-4521-a15e-9cdb4adc0b05_2F4A4060-D5E8-46A7-A278-E2DD9E4236E2.jpg?v=2a67bc9a3430e24a732f836e741d4410',
        alt: 'Morning at episode',
      },
      {
        src: 'https://storage.readdy-site.link/project_files/8e67da46-5e1d-4785-abe7-63d36243e4bc/b3e35695-c800-437f-8a8a-4ddf1c7cce20_6FA9B46F-6FCB-46D8-90C9-1CC7AB0CB859.jpg?v=01de01e0a7cbb730b3b66fcacda564a8',
        alt: 'Morning menu episode',
      },
      {
        src: 'https://storage.readdy-site.link/project_files/8e67da46-5e1d-4785-abe7-63d36243e4bc/671a9b43-564a-4e10-8011-4c56ceba1e2e_65E63C48-9A73-44C7-9EB7-77F4E11E9567.jpg?v=e6d4c25b064549bf41a94eaac163bc16',
        alt: 'Morning toast episode',
      },
      {
        src: 'https://storage.readdy-site.link/project_files/8e67da46-5e1d-4785-abe7-63d36243e4bc/feb3ed26-02ac-46a9-89b2-60e00fa865b0_FDA0E398-23ED-442E-B8A9-C52B80741A72.jpg?v=30dabd48342d23b27b01446388052128',
        alt: 'Morning drink episode',
      },
    ],
    sections: [
      {
        id: 'morning-main',
        heading: 'Morning',
        subheading: '8:00 – 11:00  (L.O. 10:45)',
        items: [
          { name: 'Butter Toast', price: '¥850' },
          { name: 'Croissant Sandwich', price: '¥850' },
          { name: 'Gluten-Free Pancakes', price: '¥1,100' },
        ],
        optionNote: '※ With Drink',
      },
      {
        id: 'morning-addons',
        heading: 'Add-Ons',
        items: [
          { name: 'Mini soup / Boiled egg / Fried egg', price: '¥120 each' },
          { name: 'Crispy bacon', price: '¥180' },
        ],
      },
    ],
  },
  {
    id: 'food',
    label: 'Food',
    images: [
      {
        src: 'https://storage.readdy-site.link/project_files/8e67da46-5e1d-4785-abe7-63d36243e4bc/88d296c1-326a-4fc4-a1e0-226c84fc922b_Lunch_Avo.jpg?v=d89ad19277ce60ee4be737a4ec72778c',
        alt: 'Avocado Toast Lunch',
      },
      {
        src: 'https://storage.readdy-site.link/project_files/8e67da46-5e1d-4785-abe7-63d36243e4bc/9e348d07-2032-4846-8e2c-19bbf3c44b77_croissant-sandwich.jpg?v=d8c6ad71563bbd3e6ee9d1b9f165c1ac',
        alt: 'Croissant Sandwich',
      },
      {
        src: 'https://storage.readdy-site.link/project_files/8e67da46-5e1d-4785-abe7-63d36243e4bc/d957bae7-f294-4c84-8695-f59943b9a344_SoupBread.jpg?v=557991c10f4c5236de8bac0c11319fb9',
        alt: 'Soup & Bread',
      },
    ],
    sections: [
      {
        id: 'lunch',
        heading: 'Lunch',
        items: [
          { name: 'Avocado Toast Plate', price: '¥1,500' },
          { name: 'Grilled Chicken Plate', price: '¥1,500' },
          { name: 'Gluten-Free Pancakes', price: '¥1,300' },
          { name: 'Soup Set', price: '+¥330', note: 'add-on' },
        ],
      },
      {
        id: 'soup',
        heading: 'Soup & Sandwich',
        items: [
          { name: 'Soup & Bread', price: '¥900' },
          { name: 'Croissant Sandwich', price: '¥680' },
        ],
      },
    ],
  },
  {
    id: 'coffee',
    label: 'Coffee',
    images: [
      {
        src: 'https://storage.readdy-site.link/project_files/8e67da46-5e1d-4785-abe7-63d36243e4bc/bade1d70-e586-4369-ad90-04236e4fc9a1_cafe-lette.jpg?v=74b5848bf7dc4cf7237851e8b8e13920',
        alt: 'Cafe Latte',
      },
      {
        src: 'https://storage.readdy-site.link/project_files/8e67da46-5e1d-4785-abe7-63d36243e4bc/29fecac1-7a93-4ff3-82d8-1e9887d76310_coconut-latte.jpg?v=d650785578c1cd707fad5aa2cb2055e2',
        alt: 'Coconut Latte',
      },
      {
        src: 'https://storage.readdy-site.link/project_files/8e67da46-5e1d-4785-abe7-63d36243e4bc/db9937d9-7728-45b0-8a3b-b9272abbef6c_Matchalatte.png?v=76b5b265af3ac4770692ea1b3523cca8',
        alt: 'Matcha Latte',
      },
    ],
    sections: [
      {
        id: 'handdrip',
        heading: 'Hand-Drip Coffee',
        subheading: 'Hot / Iced',
        items: [
          { name: 'episode select — Single Origin', price: '¥650' },
          { name: 'episode blend', price: '¥600' },
          { name: 'Decaf', price: '¥650' },
        ],
      },
      {
        id: 'espresso',
        heading: 'Espresso',
        items: [
          { name: 'Espresso', price: '¥450' },
          { name: 'Double Espresso', price: '¥600' },
          { name: 'Americano', note: 'Hot / Iced', price: '¥550' },
          { name: 'Espresso Tonic', price: '¥700' },
        ],
      },
      {
        id: 'latte',
        heading: 'Latte',
        subheading: 'Hot / Iced',
        items: [
          { name: 'Caffè Latte', price: '¥650' },
          { name: 'Oat Milk Latte', price: '¥680' },
        ],
      },
      {
        id: 'flavorlatte',
        heading: 'Flavor Latte',
        subheading: 'Hot / Iced',
        items: [
          { name: 'Caramel Latte', price: '¥680' },
          { name: 'Mocha Latte', price: '¥680' },
          { name: 'Hazelnut Latte', price: '¥680' },
          { name: 'French Vanilla Latte', price: '¥680' },
        ],
        optionNote: 'Oat milk substitution +¥50',
      },
      {
        id: 'signaturelatte',
        heading: 'Signature Latte',
        items: [
          { name: 'Matcha Latte', note: 'Hot / Iced', price: '¥700' },
          { name: 'Coconut Latte', note: 'Hot', price: '¥700' },
          { name: 'Vege Latte', note: 'Iced', price: '¥680' },
        ],
        optionNote: 'Oat milk substitution +¥50',
      },
    ],
  },
  {
    id: 'tea',
    label: 'Tea & Drinks',
    images: [
      {
        src: 'https://storage.readdy-site.link/project_files/8e67da46-5e1d-4785-abe7-63d36243e4bc/b8d6b904-5429-48d4-82b7-a9e02d8197b0_craft-ginger-ale.jpg?v=71bf40ca661da8fe2b3a7a46efaaaf0c',
        alt: 'Craft Ginger Ale',
      },
      {
        src: 'https://storage.readdy-site.link/project_files/8e67da46-5e1d-4785-abe7-63d36243e4bc/ecf5b1b6-ec04-4394-a4f0-84466f558ea4_PRANA-CHAI.jpg?v=10f7c9b6d206120cea3def3269b628c0',
        alt: 'Prana Chai',
      },
      {
        src: 'https://storage.readdy-site.link/project_files/8e67da46-5e1d-4785-abe7-63d36243e4bc/bab8d4d1-a4cc-4ca5-9fb9-8625481dbfce_ginger-ale_wilkinson.jpg?v=d4039212d531e1a79fd6bdb4e9e37aa6',
        alt: 'Ginger Ale Wilkinson',
      },
    ],
    sections: [
      {
        id: 'tea',
        heading: 'Tea',
        items: [
          { name: 'Organic Tea', note: 'Hot / Iced', price: '¥650' },
          {
            name: 'Herbal Tea',
            note: 'Hot',
            price: '¥680 each',
            variants: ['Melissa', 'Elderflower', '3 Mint Tea'],
          },
          { name: 'Prana Chai', note: 'Hot', price: '¥650' },
        ],
        optionNote: 'Oat milk substitution +¥50',
      },
      {
        id: 'other',
        heading: 'Other Drinks',
        items: [
          { name: 'Milk', price: '¥400' },
          { name: 'Oat Milk', price: '¥450' },
          { name: 'Craft Ginger Ale', price: '¥700' },
          { name: 'Hot Ginger', price: '¥700' },
          { name: 'Ginger Ale (Wilkinson)', price: '¥550' },
          { name: 'Organic Juice', note: 'Apple / Orange', price: '¥550' },
        ],
      },
    ],
  },
  {
    id: 'sweets',
    label: 'Sweets',
    images: [
      {
        src: 'https://storage.readdy-site.link/project_files/8e67da46-5e1d-4785-abe7-63d36243e4bc/81d66608-a393-4a0e-918d-9107b040fc14_brownie.jpg?v=1ab371161706a65aa8cfa9ff08d9e205',
        alt: 'Brownie',
      },
    ],
    sections: [
      {
        id: 'sweets',
        heading: 'Sweets',
        items: [
          { name: 'Shio Koji Cookie', price: '¥350' },
          {
            name: 'Oatmeal Cookie',
            price: '¥450',
            variants: ['Chocolate Chip', 'White Chocolate & Berry'],
          },
          { name: 'episode brownie', price: '¥550' },
          { name: 'Banana Bread', price: '¥580' },
          { name: 'Chocolate Terrine', price: '¥700' },
          { name: 'Lemon Tart', price: '¥700' },
        ],
      },
    ],
  },
];

// ─── Tab accent colors ────────────────────────────────────────────────────────

const TAB_COLORS: Record<string, string> = {
  morning: '#C696A8',
  food: '#386663',
  coffee: '#A790C4',
  tea: '#C696A8',
  sweets: '#386663',
};

// ─── Lunch Time Info Component ────────────────────────────────────────────────

function LunchTimeInfo({ accentColor }: { accentColor: string }) {
  return (
    <div
      className="mb-3 px-3 py-2 rounded-xl text-xs leading-relaxed"
      style={{ backgroundColor: `${accentColor}12`, fontFamily: 'serif' }}
    >
      <div className="flex items-start gap-2 mb-1.5">
        <span className="w-1.5 h-1.5 rounded-full flex-shrink-0 mt-1" style={{ backgroundColor: accentColor }}></span>
        <div>
          <span style={{ color: accentColor }}>Weekdays</span>
          <span className="text-gray-600 ml-1">11:00 – 15:00  (L.O. 14:30)</span>
        </div>
      </div>
      <div className="flex items-start gap-2">
        <span className="w-1.5 h-1.5 rounded-full flex-shrink-0 mt-1" style={{ backgroundColor: accentColor }}></span>
        <div>
          <span style={{ color: accentColor }}>Weekends &amp; Holidays</span>
          <span className="text-gray-600 ml-1">11:30 – 15:00  (L.O. 14:30)</span>
        </div>
      </div>
    </div>
  );
}

// ─── Menu Item Row ────────────────────────────────────────────────────────────

function MenuItemRow({ item, accentColor }: { item: MenuItem; accentColor: string }) {
  return (
    <div>
      <div className="flex items-baseline justify-between gap-4 py-2">
        <div className="flex items-baseline gap-2 min-w-0">
          <span className="text-sm text-gray-800 leading-relaxed" style={{ fontFamily: 'serif' }}>
            {item.name}
          </span>
          {item.note && (
            <span className="text-xs text-gray-400 whitespace-nowrap" style={{ fontFamily: 'serif' }}>
              {item.note}
            </span>
          )}
        </div>
        {item.price && (
          <span
            className="text-sm flex-shrink-0 font-light tabular-nums"
            style={{ fontFamily: 'serif', color: accentColor }}
          >
            {item.price}
          </span>
        )}
      </div>
      {item.variants && (
        <div className="pl-4 pb-1 space-y-0.5">
          {item.variants.map((v, vi) => (
            <p key={vi} className="text-xs text-gray-400" style={{ fontFamily: 'serif' }}>
              · {v}
            </p>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Section Block ────────────────────────────────────────────────────────────

function SectionBlock({ section, accentColor, showLunchTime }: { section: MenuSection; accentColor: string; showLunchTime?: boolean }) {
  return (
    <div className="mb-6 last:mb-0">
      <div className="flex items-baseline gap-3 mb-2">
        <h3
          className="text-xs font-medium tracking-widest uppercase"
          style={{ fontFamily: 'serif', color: accentColor, letterSpacing: '0.18em' }}
        >
          {section.heading}
        </h3>
        {section.subheading && (
          <span className="text-xs text-gray-400" style={{ fontFamily: 'serif' }}>
            {section.subheading}
          </span>
        )}
      </div>
      {showLunchTime && <LunchTimeInfo accentColor={accentColor} />}
      <div className="border-t border-gray-100 pt-1">
        {section.items.map((item, i) => (
          <MenuItemRow key={i} item={item} accentColor={accentColor} />
        ))}
        {section.optionNote && (
          <p className="text-xs text-gray-400 pt-2 italic" style={{ fontFamily: 'serif' }}>
            {section.optionNote}
          </p>
        )}
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function CafeMenu() {
  const [activeTab, setActiveTab] = useState('morning');
  const [activeImg, setActiveImg] = useState(0);

  const currentTab = TABS.find((t) => t.id === activeTab)!;
  const accentColor = TAB_COLORS[activeTab];

  const handleTabChange = (tabId: string) => {
    setActiveTab(tabId);
    setActiveImg(0);
  };

  return (
    <div className="w-full max-w-4xl mx-auto">

      {/* Tab Bar */}
      <div className="flex items-center justify-center gap-1 mb-10 flex-wrap">
        {TABS.map((tab) => {
          const isActive = tab.id === activeTab;
          const color = TAB_COLORS[tab.id];
          return (
            <button
              key={tab.id}
              onClick={() => handleTabChange(tab.id)}
              className="px-6 py-2 text-xs tracking-widest uppercase cursor-pointer transition-all duration-300 rounded-full whitespace-nowrap"
              style={{
                fontFamily: 'serif',
                letterSpacing: '0.15em',
                backgroundColor: isActive ? color : 'transparent',
                color: isActive ? '#ffffff' : '#9CA3AF',
                border: `1.5px solid ${isActive ? color : '#E5E7EB'}`,
              }}
            >
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Main Content: side-by-side */}
      <div
        className="bg-white rounded-3xl border border-gray-100 overflow-hidden"
        style={{ transition: 'box-shadow 0.3s' }}
      >
        <div className="flex flex-col md:flex-row min-h-[520px]">

          {/* Left — Image Gallery */}
          <div className="relative w-full md:w-[42%] flex-shrink-0 overflow-hidden bg-gray-50" style={{ minHeight: '380px' }}>
            {/* Image slides */}
            {currentTab.images.map((img, i) => (
              <div
                key={i}
                className="absolute inset-0 transition-opacity duration-700"
                style={{ opacity: activeImg === i ? 1 : 0 }}
              >
                <img
                  src={img.src}
                  alt={img.alt}
                  className="w-full h-full object-cover object-center md:object-top"
                />
                {/* subtle dark gradient on bottom */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/25 via-transparent to-transparent"></div>
              </div>
            ))}

            {/* Thumbnail strip at bottom */}
            <div className="absolute bottom-4 left-0 right-0 flex items-center justify-center gap-2 z-10">
              {currentTab.images.map((img, i) => (
                <button
                  key={i}
                  onClick={() => setActiveImg(i)}
                  className="transition-all duration-300 rounded-xl overflow-hidden cursor-pointer flex-shrink-0"
                  style={{
                    width: activeImg === i ? '52px' : '40px',
                    height: activeImg === i ? '36px' : '28px',
                    outline: activeImg === i ? `2px solid ${accentColor}` : '2px solid transparent',
                    outlineOffset: '2px',
                    opacity: activeImg === i ? 1 : 0.65,
                    transition: 'all 0.3s',
                  }}
                  aria-label={img.alt}
                >
                  <img src={img.src} alt={img.alt} className="w-full h-full object-cover object-center md:object-top" />
                </button>
              ))}
            </div>

            {/* Nav arrows */}
            {currentTab.images.length > 1 && (
              <>
                <button
                  onClick={() => setActiveImg((prev) => (prev - 1 + currentTab.images.length) % currentTab.images.length)}
                  className="absolute left-3 top-1/2 -translate-y-1/2 z-10 w-8 h-8 flex items-center justify-center rounded-full bg-white/70 hover:bg-white transition-all cursor-pointer"
                  aria-label="Previous image"
                >
                  <i className="ri-arrow-left-s-line text-gray-700 text-lg"></i>
                </button>
                <button
                  onClick={() => setActiveImg((prev) => (prev + 1) % currentTab.images.length)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 z-10 w-8 h-8 flex items-center justify-center rounded-full bg-white/70 hover:bg-white transition-all cursor-pointer"
                  aria-label="Next image"
                >
                  <i className="ri-arrow-right-s-line text-gray-700 text-lg"></i>
                </button>
              </>
            )}
          </div>

          {/* Right — Menu Sections */}
          <div className="flex-1 px-8 py-8 overflow-y-auto" style={{ maxHeight: '580px' }}>
            {/* Tab title */}
            <div className="flex items-center gap-2 mb-6">
              <div
                className="w-1 h-5 rounded-full flex-shrink-0"
                style={{ backgroundColor: accentColor }}
              ></div>
              <h3
                className="text-xs tracking-widest uppercase font-light text-gray-900"
                style={{ fontFamily: 'serif', letterSpacing: '0.25em' }}
              >
                {currentTab.label}
              </h3>
            </div>

            {currentTab.sections.map((section) => (
              <SectionBlock
                key={section.id}
                section={section}
                accentColor={accentColor}
                showLunchTime={section.id === 'lunch'}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Bottom note */}
      <div className="mt-6 text-center px-4">
        <p
          className="text-xs text-gray-600 leading-relaxed mb-1"
          style={{ fontFamily: 'serif', lineHeight: '1.9' }}
        >
          レンタル利用時のメニュー内容は、<br className="md:hidden" />イベントの形式によって異なる場合があります。
        </p>
        <p
          className="text-xs text-gray-400 leading-relaxed italic"
          style={{ fontFamily: 'serif', lineHeight: '1.9' }}
        >
          Menu availability during rental use<br className="md:hidden" /> may vary depending on the event style.
        </p>
      </div>
    </div>
  );
}
